/**
 * Constantes globales partagees par le moteur, l'API et l'interface.
 *
 * Toute modification d'une constante marquee « MOTEUR » change le resultat des
 * simulations : elle doit s'accompagner d'une incrementation de ENGINE_VERSION,
 * sans quoi les sauvegardes et les rejeux existants deviendraient incoherents.
 */

/**
 * Version du moteur de simulation. Incrementee des qu'un changement modifie le
 * resultat d'une partie a graine et journal d'ordres identiques.
 */
export const ENGINE_VERSION = 1;

/** MOTEUR - duree de jeu simulee par tick, en secondes. */
export const SIM_TICK_SECONDS = 5;

/** MOTEUR - cote d'une cellule de la grille, en metres. */
export const DEFAULT_CELL_SIZE_M = 20;

/** Dimensions de grille autorisees (bornes de securite pour les scenarios). */
export const MIN_GRID_SIZE = 64;
export const MAX_GRID_SIZE = 512;

/** Vitesses de jeu proposees au joueur. */
export const GAME_SPEEDS = ['PAUSE', 'X1', 'X2', 'X4', 'X8'] as const;
export type GameSpeed = (typeof GAME_SPEEDS)[number];

/**
 * Nombre de ticks de simulation executes par seconde reelle pour chaque vitesse.
 * La duree simulee d'un tick ne change jamais : seule la cadence varie, ce qui
 * garantit un resultat identique quelle que soit la vitesse choisie.
 */
export const TICKS_PER_SECOND: Record<GameSpeed, number> = {
  PAUSE: 0,
  X1: 4,
  X2: 8,
  X4: 16,
  X8: 32,
};

/** Facteur d'acceleration percu par le joueur (temps de jeu / temps reel). */
export function speedFactor(speed: GameSpeed): number {
  return TICKS_PER_SECOND[speed] * SIM_TICK_SECONDS;
}

/** Surface d'une cellule en hectares, pour l'affichage des surfaces brulees. */
export function cellAreaHectares(cellSizeM: number = DEFAULT_CELL_SIZE_M): number {
  return (cellSizeM * cellSizeM) / 10_000;
}

/** Longueurs maximales acceptees par l'API (validation et garde-fous UI). */
export const LIMITS = {
  emailMax: 254,
  passwordMin: 10,
  passwordMax: 128,
  displayNameMin: 2,
  displayNameMax: 32,
  saveNameMax: 60,
  scenarioSlugMax: 64,
  scenarioTitleMax: 120,
  /** Taille maximale d'une sauvegarde compressee acceptee par l'API, en octets. */
  saveBlobMaxBytes: 4 * 1024 * 1024,
  /** Nombre de slots de sauvegarde manuels par scenario et par joueur. */
  saveSlotsPerScenario: 3,
} as const;
